/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import envConfig from '../config/envConfig';
import Button from './Button';
import { useNavigate } from 'react-router-dom';
import GaneshaLogo from './GaneshaLogo';

const mandatoryOrg = [
  'name', 'description', 'city', 'state', 'country', 'pincode', 'tax_id'
];

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9 ]*$/; // letters, numbers, spaces
const PINCODE_REGEX = /^[0-9]*$/;

const mandatoryOrgFields = [
  { label: "Organization Name", name: "name", maxLength: 64 },
  { label: "Description", name: "description" },
  { label: "Registered Address", name: "registered_address" },
  { label: "City", name: "city", maxLength: 100 },
  { label: "State", name: "state", maxLength: 100 },
  { label: "Country", name: "country", maxLength: 100 },
  { label: "Pincode", name: "pincode", maxLength: 20, numericOnly: true },
  { label: "Tax ID", name: "tax_id" },
  { label: "Govt ID 1", name: "government_id1" },
  { label: "Govt ID 2", name: "government_id2" }
];

function validatePassword(password) {
  const errors = [];
  if (password.length < 8) errors.push("Minimum 8 characters");
  if (!/[A-Z]/.test(password)) errors.push("At least one uppercase letter");
  if (!/\d/.test(password)) errors.push("At least one number");
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push("At least one special character");
  return errors;
}

function validateDomain(domain) {
  const trimmed = domain.trim();
  if (!trimmed) {
    return "Domain name is required.";
  }

  // Reject if it looks like a URL with protocol
  if (/^https?:\/\//i.test(trimmed)) {
    return "Enter domain without http/https (e.g. example.com).";
  }

  // Basic domain regex: labels + dot + TLD (2+ chars)
  const domainRegex = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;
  if (!domainRegex.test(trimmed)) {
    return "Please enter a valid domain (e.g. example.com).";
  }

  return "";
}

function OrganizationRegistrationForm({ onSubmit }) {
  const { theme } = useTheme();

  const [org, setOrg] = useState({
    name: '', description: '', registered_address: '',
    city: '', state: '', country: '', pincode: '',
    tax_id: '', government_id1: '', government_id2: ''
  });
  const [admin, setAdmin] = useState({
    username: '', email: '', password: '', confirmPassword: '', first_name: '', last_name: ''
  });

  const [domainName, setDomainName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const errorTimeouts = useRef({});
  const navigate = useNavigate();
  const [passwordFeedback, setPasswordFeedback] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Realtime password validation feedback
    setPasswordFeedback(validatePassword(admin.password));
  }, [admin.password]);

  // Fade error feedback
  const clearErrorAfterDelay = (field, delay = 4000) => {
    if (errorTimeouts.current[field]) clearTimeout(errorTimeouts.current[field]);
    errorTimeouts.current[field] = setTimeout(() => {
      setErrors(prev => ({ ...prev, [field]: '' }));
      delete errorTimeouts.current[field];
    }, delay);
  };

  // Validate fields for both organization and admin section
  const validateFields = () => {
    let errs = {};

    const domainError = validateDomain(domainName);
    if (domainError) {
      errs.domain_name = domainError;
    }

    for (const key of mandatoryOrg) {
      if (!org[key].trim()) errs['org_' + key] = `${key.replace('_', ' ')} is required.`;
    }

    // Alphanumeric-only org fields (letters/numbers/spaces)
    ['name', 'city', 'state', 'country'].forEach((field) => {
      const value = org[field];
      if (value && !ALPHANUMERIC_REGEX.test(value)) {
        errs['org_' + field] =
          `${field.charAt(0).toUpperCase() + field.slice(1)} can only contain letters, numbers and spaces.`;
      }
    });

    // Pincode numeric-only + max length (20)
    if (org.pincode) {
      if (!PINCODE_REGEX.test(org.pincode)) {
        errs.org_pincode = "Pincode must contain only digits.";
      } else if (org.pincode.length > 20) {
        errs.org_pincode = "Pincode must be at most 20 digits.";
      }
    }

    // Org field max lengths (from model)
    if (org.name && org.name.length > 64) {
      errs.org_name = "Organization name must be at most 64 characters long.";
    }
    ['city', 'state', 'country'].forEach((field) => {
      if (org[field] && org[field].length > 100) {
        errs['org_' + field] =
          `${field.charAt(0).toUpperCase() + field.slice(1)} must be at most 100 characters long.`;
      }
    });

    if (!admin.username.trim()) {
      errs.admin_username = "Username is required.";
    } else {
      if (admin.username.length > 30) {
        errs.admin_username = "Username must be at most 30 characters long.";
      } else if (!USERNAME_REGEX.test(admin.username)) {
        errs.admin_username = "Username must be alphanumeric and can include underscores.";
      }
    }

    if (!admin.email.trim()) {
      errs.admin_email = "Email is required.";
    } else if (!EMAIL_REGEX.test(admin.email)) {
      errs.admin_email = "Please enter a valid email address.";
    }

    if (!admin.password.trim()) errs.admin_password = "Password is required.";
    const pwErrors = validatePassword(admin.password);
    if (pwErrors.length) errs.admin_password = "Password: " + pwErrors.join(', ');
    if (!admin.confirmPassword.trim()) errs.admin_confirmPassword = "Please confirm your password.";
    if (admin.password !== admin.confirmPassword) errs.admin_confirmPassword = "Passwords do not match.";
    if (!admin.first_name.trim()) errs.admin_first_name = "First name is required.";
    // last_name is optional
    return errs;
  };

  // Clear errors on fields after a delay
  useEffect(() => {
    Object.keys(errors).forEach(field => errors[field] && clearErrorAfterDelay(field));
  }, [errors]);

  const handleChange = (e, section) => {
    const { name, value } = e.target;

    // Numeric-only for pincode (or any field with numericOnly flag)
    if (section === 'org') {
      const fieldConfig = mandatoryOrgFields?.find(f => f.name === name);
      if (fieldConfig?.numericOnly) {
        if (!/^\d*$/.test(value)) return; // ignore invalid input
      }
      setOrg(prev => ({ ...prev, [name]: value }));
    } else {
      setAdmin(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let errs = validateFields();
    setErrors(errs);
    if (Object.keys(errs).length) {
      Object.keys(errs).forEach(clearErrorAfterDelay);
      return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...adminData } = admin;
      await onSubmit(org, adminData, domainName, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column align-items-center justify-content-center"
      style={{
        background: theme.gradientBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}
    >
      <div
        className="px-4 py-5 shadow-lg"
        style={{
          width: 900,
          maxWidth: '100%',
          background: '#fff',
          borderRadius: theme.cardRadius,
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.2)'
        }}
      >
        {/* Top Back Button */}
        <Button
          mode="secondary"
          size="sm"
          onClick={() => navigate('/')}
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: 20,
            left: 20,
            borderRadius: '50%',
            width: 38,
            height: 38,
            padding: 0,
            minWidth: 0,
            background: 'transparent',
            border: `1px solid ${theme.primary}20`,
            color: theme.primary
          }}
        >
          <i className="bi bi-arrow-left fs-5" />
        </Button>

        {/* Logo and App Name */}
        <div className="d-flex flex-column align-items-center mb-4 mt-2">
          <div className="p-3 rounded-circle mb-3" style={{ background: `${theme.primary}10` }}>
            <GaneshaLogo height={50} width={50} color={theme.primary} />
          </div>
          <h3
            className="mb-0"
            style={{
              color: theme.primary,
              fontWeight: 800,
              letterSpacing: '-0.5px'
            }}
          >
            {envConfig.appName}
          </h3>
          <p className="text-secondary small mb-0 fw-medium">Start Building Today</p>
        </div>

        <form onSubmit={handleSubmit} className="mt-4">
          <h4 className="mb-4 fw-bold text-center" style={{ color: theme.primary }}>
            Create Organization & Admin
          </h4>

          <div className="row g-4">
            {/* Organization Fields */}
            <div className="col-md-6 col-12">
              <div className="p-4 rounded-3 h-100" style={{ background: '#f8f9fa', border: `1px solid ${theme.primary}10` }}>
                <h6 className="mb-3 d-flex align-items-center fw-bold" style={{ color: theme.primary }}>
                  <i className="bi bi-building me-2"></i> Organization Details
                </h6>

                <div className="form-floating mb-3">
                  <input
                    type="text"
                    name="domain_name"
                    id="domain_name"
                    className="form-control"
                    placeholder="example.com"
                    value={domainName}
                    onChange={e => setDomainName(e.target.value)}
                    style={{
                      borderRadius: theme.buttonRadius,
                      border: `1px solid ${theme.primary}20`
                    }}
                  />
                  <label htmlFor="domain_name">Domain Name (e.g. example.com) *</label>
                  {errors.domain_name && (
                    <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                      <i className="bi bi-exclamation-circle me-1"></i> {errors.domain_name}
                    </div>
                  )}
                </div>

                {mandatoryOrgFields.map(field => (
                  <div className="form-group mb-3" key={field.name}>
                    <label className="form-label small fw-semibold text-muted mb-1">
                      {field.label} {mandatoryOrg.includes(field.name) && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="text"
                      name={field.name}
                      className="form-control"
                      value={org[field.name] || ""}
                      onChange={e => handleChange(e, "org")}
                      maxLength={field.maxLength}
                      inputMode={field.numericOnly ? "numeric" : "text"}
                      style={{
                        borderRadius: theme.buttonRadius,
                        border: `1px solid ${theme.primary}20`
                      }}
                    />
                    {errors['org_' + field.name] && (
                      <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                        <i className="bi bi-exclamation-circle me-1"></i> {errors['org_' + field.name]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Admin Section */}
            <div className="col-md-6 col-12">
              <div className="p-4 rounded-3 h-100" style={{ background: '#f8f9fa', border: `1px solid ${theme.primary}10` }}>
                <h6 className="mb-3 d-flex align-items-center fw-bold" style={{ color: theme.primary }}>
                  <i className="bi bi-person-badge me-2"></i> Admin Account
                </h6>

                {[
                  { label: "Username", name: "username", maxLength: 30 },
                  { label: "Email", name: "email" }
                ].map(field => (
                  <div className="form-group mb-3" key={field.name}>
                    <label className="form-label small fw-semibold text-muted mb-1">
                      {field.label} <span className="text-danger">*</span>
                    </label>
                    <input
                      type={field.inputType || "text"}
                      name={field.name}
                      className="form-control"
                      value={admin[field.name] || ""}
                      onChange={e => handleChange(e, "admin")}
                      maxLength={field.maxLength}
                      style={{
                        borderRadius: theme.buttonRadius,
                        border: `1px solid ${theme.primary}20`
                      }}
                    />
                    {errors['admin_' + field.name] && (
                      <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                        <i className="bi bi-exclamation-circle me-1"></i> {errors['admin_' + field.name]}
                      </div>
                    )}
                  </div>
                ))}

                {/* Password */}
                <div className="form-group mb-3 relative">
                  <label className="form-label small fw-semibold text-muted mb-1">
                    Password <span className="text-danger">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="form-control no-edge-eye"
                      value={admin.password || ""}
                      onChange={e => handleChange(e, "admin")}
                      style={{
                        borderRadius: theme.buttonRadius,
                        border: `1px solid ${theme.primary}20`,
                        paddingRight: 40
                      }}
                      autoComplete="new-password"
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: theme.primary
                      }}
                      onClick={() => setShowPassword(v => !v)}
                    >
                      <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
                    </span>
                  </div>

                  {passwordFeedback.length > 0 && (
                    <div className="mt-2 p-2 rounded bg-white border small text-muted">
                      <ul className="mb-0 ps-3">
                        {passwordFeedback.map((rule, idx) => (
                          <li key={idx} className="text-warning">{rule}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {errors.admin_password && (
                    <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                      <i className="bi bi-exclamation-circle me-1"></i> {errors.admin_password}
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="form-group mb-3">
                  <label className="form-label small fw-semibold text-muted mb-1">
                    Confirm Password <span className="text-danger">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className="form-control no-edge-eye"
                      value={admin.confirmPassword || ""}
                      onChange={e => handleChange(e, "admin")}
                      style={{
                        borderRadius: theme.buttonRadius,
                        border: `1px solid ${theme.primary}20`,
                        paddingRight: 40
                      }}
                      autoComplete="new-password"
                    />
                    <span
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: theme.primary
                      }}
                      onClick={() => setShowConfirmPassword(v => !v)}
                    >
                      <i className={showConfirmPassword ? "bi bi-eye-slash" : "bi bi-eye"} />
                    </span>
                  </div>
                  {errors.admin_confirmPassword && (
                    <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                      <i className="bi bi-exclamation-circle me-1"></i> {errors.admin_confirmPassword}
                    </div>
                  )}
                  {admin.confirmPassword && admin.password !== admin.confirmPassword && (
                    <div className="d-flex align-items-center mt-1 text-warning small">
                      Passwords do not match.
                    </div>
                  )}
                </div>

                {/* First Name */}
                <div className="form-group mb-3">
                  <label className="form-label small fw-semibold text-muted mb-1">
                    First Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    className="form-control"
                    value={admin.first_name || ""}
                    onChange={e => handleChange(e, "admin")}
                    style={{
                      borderRadius: theme.buttonRadius,
                      border: `1px solid ${theme.primary}20`
                    }}
                  />
                  {errors.admin_first_name && (
                    <div className="d-flex align-items-center mt-1" style={{ color: "#dc3545", fontSize: 13 }}>
                      <i className="bi bi-exclamation-circle me-1"></i> {errors.admin_first_name}
                    </div>
                  )}
                </div>

                {/* Last Name */}
                <div className="form-group mb-3">
                  <label className="form-label small fw-semibold text-muted mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    className="form-control"
                    value={admin.last_name || ""}
                    onChange={e => handleChange(e, "admin")}
                    style={{
                      borderRadius: theme.buttonRadius,
                      border: `1px solid ${theme.primary}20`
                    }}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* API error, if any */}
          {errors.api_error && (
            <div className="alert alert-danger mt-3 text-center py-2 icon-link-hover">
              <i className="bi bi-exclamation-triangle-fill me-2"></i> {errors.api_error}
            </div>
          )}

          <div className="d-grid mt-4">
            <Button
              mode="primary"
              type="submit"
              disabled={loading}
              className="py-3 fs-5 fw-bold shadow"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Creating your Workspace...
                </>
              ) : 'Complete Registration'}
            </Button>
          </div>
          <div className="text-center mt-3">
            <p className="small text-muted mb-0">
              Already have an account?{' '}
              <span
                style={{ color: theme.primary, cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => navigate('/sign-in')}
              >
                Sign In
              </span>
            </p>
          </div>
        </form>
      </div>

      {/* Footer text */}
      <div className="mt-4 text-white opacity-75 small">
        &copy; {new Date().getFullYear()} {envConfig.appName}. All rights reserved.
      </div>

    </div>
  );
}

export default OrganizationRegistrationForm;
